import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit2, Loader2, Copy, Check, X, TrendingUp } from "lucide-react";

const DISCOUNT_TYPES = [
  { value: "percent", label: "Процент (%)", hint: "0-100" },
  { value: "fixed", label: "Фиксирана сума (EUR)", hint: "напр. 10" },
  { value: "credits", label: "Бонус кредити", hint: "напр. 50" },
  { value: "trial_days", label: "Дни пробен период", hint: "напр. 14" },
  { value: "patient_gift", label: "Подарък за пациент", hint: "комбиниран (генерирайте масово по-долу)" },
];

const APPLIES_TO = [
  { value: "any", label: "Навсякъде" },
  { value: "signup", label: "Само при регистрация" },
  { value: "checkout", label: "Само при покупка" },
];

const EMPTY_FORM = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  description: "",
  applies_to: "any",
  max_uses: "",
  max_uses_per_user: 1,
  valid_from: "",
  valid_until: "",
  active: true,
};

export default function AdminPromoCodes({ API, isDark }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [redemptionsFor, setRedemptionsFor] = useState(null);
  const [redemptions, setRedemptions] = useState([]);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setItems(data.items || []);
    } catch {
      toast.error("Неуспешно зареждане на промо кодове");
    }
    setLoading(false);
  }, [API]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const startEdit = (item) => {
    setEditingCode(item.code);
    setForm({
      code: item.code,
      discount_type: item.discount_type,
      discount_value: item.discount_value,
      description: item.description || "",
      applies_to: item.applies_to || "any",
      max_uses: item.max_uses ?? "",
      max_uses_per_user: item.max_uses_per_user ?? 1,
      valid_from: item.valid_from || "",
      valid_until: item.valid_until || "",
      active: item.active !== false,
    });
  };

  const resetForm = () => {
    setEditingCode(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.code.trim()) return toast.error("Код е задължителен");
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_value: parseFloat(form.discount_value) || 0,
        max_uses: form.max_uses === "" ? null : parseInt(form.max_uses, 10),
        max_uses_per_user: parseInt(form.max_uses_per_user, 10) || 1,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
      };
      if (editingCode) {
        await axios.put(`${API}/admin/promo-codes/${editingCode}`, payload);
        toast.success("Кодът е обновен");
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload);
        toast.success("Кодът е създаден");
      }
      resetForm();
      fetchCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис");
    }
    setSaving(false);
  };

  const remove = async (code) => {
    if (!window.confirm(`Изтриване на код ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`);
      toast.success("Изтрит");
      fetchCodes();
    } catch {
      toast.error("Грешка при изтриване");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1500);
  };

  const viewRedemptions = async (code) => {
    if (redemptionsFor === code) {
      setRedemptionsFor(null);
      return;
    }
    setRedemptionsFor(code);
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes/${code}/redemptions`);
      setRedemptions(data.items || []);
    } catch {
      setRedemptions([]);
    }
  };

  const descr = (item) => {
    const t = item.discount_type;
    const v = item.discount_value;
    if (t === "percent") return `${v}% отстъпка`;
    if (t === "fixed") return `€${v} отстъпка`;
    if (t === "credits") return `${v} бонус кредита`;
    if (t === "trial_days") return `+${v} дни пробен период`;
    if (t === "patient_gift") {
      const bc = item.bonus_credits || 0;
      const td = item.trial_days || 0;
      const dl = item.daily_credit_limit || 0;
      return `Подарък: ${bc} кредита + ${td} дни${dl ? ` · лимит ${dl}/ден` : ""}`;
    }
    return "—";
  };

  const cellCls = isDark ? "text-gray-200" : "text-gray-800";
  const labelCls = `text-[11px] font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`;
  const inputCls = `h-9 text-sm ${isDark ? "bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white" : ""}`;

  return (
    <div className="space-y-5" data-testid="admin-promo-codes">
      <PatientGiftBulkSection API={API} isDark={isDark} onCreated={fetchCodes}
        cellCls={cellCls} labelCls={labelCls} inputCls={inputCls} />
      {/* Create/Edit Form */}
      <Card className={isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-bold ${cellCls}`}>
              {editingCode ? `Редактиране на ${editingCode}` : "Нов промо код"}
            </h3>
            {editingCode && (
              <Button variant="ghost" size="sm" onClick={resetForm} data-testid="promo-cancel-edit-btn">
                <X className="w-4 h-4 mr-1" /> Отказ
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Код (UPPERCASE)</label>
              <Input
                value={form.code}
                onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="напр. LAUNCH2026"
                disabled={!!editingCode}
                className={inputCls}
                data-testid="promo-code-input"
              />
            </div>
            <div>
              <label className={labelCls}>Тип отстъпка</label>
              <select value={form.discount_type}
                onChange={(e) => setForm(f => ({ ...f, discount_type: e.target.value }))}
                className={`w-full h-9 rounded-md border px-2 text-sm ${isDark ? "bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white" : "bg-white border-gray-200"}`}
                data-testid="promo-type-select"
              >
                {DISCOUNT_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Стойност</label>
              <Input type="number" value={form.discount_value}
                onChange={(e) => setForm(f => ({ ...f, discount_value: e.target.value }))}
                className={inputCls} data-testid="promo-value-input"
              />
            </div>
            <div className="md:col-span-3">
              <label className={labelCls}>Описание (видимо за админ)</label>
              <Input value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="напр. Промоция за стартиране — 20% от първа година"
                className={inputCls} data-testid="promo-desc-input"
              />
            </div>
            <div>
              <label className={labelCls}>Приложимо за</label>
              <select value={form.applies_to}
                onChange={(e) => setForm(f => ({ ...f, applies_to: e.target.value }))}
                className={`w-full h-9 rounded-md border px-2 text-sm ${isDark ? "bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white" : "bg-white border-gray-200"}`}
                data-testid="promo-appliesto-select"
              >
                {APPLIES_TO.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Макс. общо ползвания (празно = неограничено)</label>
              <Input type="number" value={form.max_uses}
                onChange={(e) => setForm(f => ({ ...f, max_uses: e.target.value }))}
                placeholder="няма лимит" className={inputCls} data-testid="promo-maxuses-input"
              />
            </div>
            <div>
              <label className={labelCls}>Макс. на потребител</label>
              <Input type="number" value={form.max_uses_per_user}
                onChange={(e) => setForm(f => ({ ...f, max_uses_per_user: e.target.value }))}
                className={inputCls} data-testid="promo-peruser-input"
              />
            </div>
            <div>
              <label className={labelCls}>Валиден от</label>
              <Input type="datetime-local" value={form.valid_from ? form.valid_from.slice(0, 16) : ""}
                onChange={(e) => setForm(f => ({ ...f, valid_from: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Валиден до</label>
              <Input type="datetime-local" value={form.valid_until ? form.valid_until.slice(0, 16) : ""}
                onChange={(e) => setForm(f => ({ ...f, valid_until: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                className={inputCls}
              />
            </div>
            <div className="flex items-center gap-2 md:col-span-3">
              <input id="promo-active" type="checkbox" checked={form.active}
                onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))}
                data-testid="promo-active-toggle"
              />
              <label htmlFor="promo-active" className={`text-sm ${cellCls}`}>Активен</label>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="promo-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {editingCode ? "Обнови" : "Създай код"}
          </Button>
        </CardContent>
      </Card>

      {/* List of codes */}
      <Card className={isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}>
        <CardContent className="p-4">
          <h3 className={`text-sm font-bold mb-3 ${cellCls}`}>Активни промо кодове ({items.length})</h3>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : items.length === 0 ? (
            <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Няма създадени кодове.</p>
          ) : (
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.code} className={`p-3 rounded-lg border ${isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)]" : "bg-gray-50 border-gray-100"}`} data-testid={`promo-row-${item.code}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => copyCode(item.code)}
                          className={`font-mono font-bold text-sm px-2 py-0.5 rounded flex items-center gap-1 ${isDark ? "bg-[hsl(223,47%,16%)] text-[#A5F3FC]" : "bg-cyan-50 text-cyan-700"}`}
                          title="Копирай"
                        >
                          {item.code}
                          {copiedCode === item.code ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 opacity-60" />}
                        </button>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {item.active ? "Активен" : "Деактивиран"}
                        </span>
                        <span className={`text-xs font-semibold ${cellCls}`}>{descr(item)}</span>
                        <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                          · {APPLIES_TO.find(a => a.value === item.applies_to)?.label}
                        </span>
                      </div>
                      {item.description && (
                        <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{item.description}</p>
                      )}
                      <p className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        Ползвания: {item.times_used || 0}{item.max_uses ? ` / ${item.max_uses}` : ""} · на потребител: {item.max_uses_per_user || 1}
                        {item.valid_until ? ` · до ${new Date(item.valid_until).toLocaleDateString("bg-BG")}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => viewRedemptions(item.code)} title="Ползвания" data-testid={`promo-stats-${item.code}`}>
                        <TrendingUp className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => startEdit(item)} data-testid={`promo-edit-${item.code}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(item.code)} data-testid={`promo-delete-${item.code}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {redemptionsFor === item.code && (
                    <div className={`mt-3 pt-3 border-t ${isDark ? "border-[hsl(223,47%,18%)]" : "border-gray-200"}`}>
                      <p className={`text-[11px] font-semibold mb-1.5 ${cellCls}`}>История на ползвания ({redemptions.length})</p>
                      {redemptions.length === 0 ? (
                        <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>Няма ползвания.</p>
                      ) : (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {redemptions.map(r => (
                            <div key={r.id} className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                              {r.user_email} · {new Date(r.applied_at).toLocaleString("bg-BG")}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ─── iter1118 — Масово генериране на „Подаръци за пациенти" ─── */
function PatientGiftBulkSection({ API, isDark, onCreated, cellCls, labelCls, inputCls }) {
  const [open, setOpen] = useState(true);
  const [form, setForm] = useState({
    count: 20,
    prefix: "PAT",
    bonus_credits: 200,
    trial_days: 60,
    daily_credit_limit: 10,
    description: "Пациенти — безплатен достъп",
    valid_days: 180,
    batch_label: "",
  });
  const [created, setCreated] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes/batches`);
      setBatches(data.items || []);
    } catch { /* тих провал — секцията е незадължителна */ }
  }, [API]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const generate = async () => {
    if (!form.count || form.count < 1) { toast.error("Броят трябва да е поне 1."); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.batch_label) delete payload.batch_label;
      const { data } = await axios.post(`${API}/admin/promo-codes/bulk-generate`, payload);
      setCreated(data.created || []);
      toast.success(`Генерирани са ${data.count} кода.`);
      await loadBatches();
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при генериране.");
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    const text = created.map(c => c.code).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Копирани в клипборда.");
  };

  const downloadCsv = async (batch) => {
    const url = batch
      ? `${API}/admin/promo-codes/export?batch=${encodeURIComponent(batch)}`
      : `${API}/admin/promo-codes/export`;
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `promo_codes${batch ? "_" + batch : ""}.csv`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Неуспешно сваляне.");
    }
  };

  return (
    <Card className={isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}
          data-testid="admin-patient-gift-section">
      <CardContent className="p-4 space-y-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={`w-full flex items-center justify-between text-left`}
          data-testid="patient-gift-toggle"
        >
          <h3 className={`text-sm font-bold ${cellCls}`}>
            Подаръци за пациенти — масово генериране
          </h3>
          <div className="flex items-center gap-3">
            <a
              href="/admin/gifts-adoption"
              className="text-[11px] font-semibold text-sky-500 hover:underline"
              onClick={(e) => e.stopPropagation()}
              data-testid="patient-gift-adoption-link"
            >
              Виж усвояването →
            </a>
            <span className={labelCls}>{open ? "▲" : "▼"}</span>
          </div>
        </button>

        {open && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Брой кодове</label>
                <Input type="number" min="1" max="500" value={form.count}
                  onChange={(e) => setForm(f => ({ ...f, count: parseInt(e.target.value) || 0 }))}
                  className={inputCls} data-testid="patient-gift-count" />
              </div>
              <div>
                <label className={labelCls}>Представка</label>
                <Input value={form.prefix}
                  onChange={(e) => setForm(f => ({ ...f, prefix: e.target.value.toUpperCase().slice(0, 6) }))}
                  className={inputCls} data-testid="patient-gift-prefix" />
              </div>
              <div>
                <label className={labelCls}>Бонус кредити</label>
                <Input type="number" min="1" value={form.bonus_credits}
                  onChange={(e) => setForm(f => ({ ...f, bonus_credits: parseInt(e.target.value) || 0 }))}
                  className={inputCls} data-testid="patient-gift-credits" />
              </div>
              <div>
                <label className={labelCls}>Пробни дни</label>
                <Input type="number" min="0" value={form.trial_days}
                  onChange={(e) => setForm(f => ({ ...f, trial_days: parseInt(e.target.value) || 0 }))}
                  className={inputCls} data-testid="patient-gift-days" />
              </div>
              <div>
                <label className={labelCls}>Дневен лимит</label>
                <Input type="number" min="0" value={form.daily_credit_limit}
                  onChange={(e) => setForm(f => ({ ...f, daily_credit_limit: parseInt(e.target.value) || 0 }))}
                  className={inputCls} data-testid="patient-gift-daily" />
              </div>
              <div>
                <label className={labelCls}>Валиден дни</label>
                <Input type="number" min="1" value={form.valid_days}
                  onChange={(e) => setForm(f => ({ ...f, valid_days: parseInt(e.target.value) || 0 }))}
                  className={inputCls} data-testid="patient-gift-valid" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Партида (незадължително)</label>
                <Input value={form.batch_label}
                  onChange={(e) => setForm(f => ({ ...f, batch_label: e.target.value }))}
                  placeholder="напр. Февруари 2026 — първа кохорта"
                  className={inputCls} data-testid="patient-gift-batch" />
              </div>
              <div className="md:col-span-4">
                <label className={labelCls}>Описание</label>
                <Input value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  className={inputCls} data-testid="patient-gift-desc" />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={generate} disabled={loading}
                data-testid="patient-gift-generate-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Генерирай {form.count} кода
              </Button>
            </div>

            {created.length > 0 && (
              <div className={`mt-3 p-3 rounded-lg ${isDark ? "bg-[hsl(223,47%,15%)]" : "bg-gray-50"}`}
                   data-testid="patient-gift-created-list">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${cellCls}`}>
                    Генерирани кодове ({created.length}):
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={copyAll}
                      data-testid="patient-gift-copy-all">
                      <Copy className="w-3 h-3 mr-1" /> Копирай всички
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => downloadCsv(form.batch_label || batches[0]?.batch)}
                      data-testid="patient-gift-download-csv">
                      Свали CSV
                    </Button>
                  </div>
                </div>

                <p className={`text-[11px] ${labelCls} mb-2`}>
                  Платформата не изпраща имейли с кодове. Копирайте всеки код и го изпратете
                  на пациента от Вашия имейл.
                </p>

                {/* Списък код по код — само копиране */}
                <div className="space-y-1.5 mt-2">
                  {created.map((c) => (
                    <CopyCodeRow key={c.code} code={c.code}
                      isDark={isDark} cellCls={cellCls} />
                  ))}
                </div>
              </div>
            )}

            {batches.length > 0 && (
              <div className="mt-4">
                <h4 className={`text-xs font-bold mb-2 ${cellCls}`}>
                  Партиди (общо {batches.length})
                </h4>
                <div className="space-y-1">
                  {batches.map((b) => (
                    <div key={b.batch}
                         className={`flex items-center justify-between p-2 rounded ${isDark ? "bg-[hsl(223,47%,15%)]" : "bg-gray-50"}`}
                         data-testid={`batch-row-${b.batch}`}>
                      <div className={`text-xs ${cellCls}`}>
                        <strong>{b.batch}</strong>
                        <span className={labelCls}>
                          {" · "}{b.used}/{b.total} използвани
                          {" · "}{b.bonus_credits} кредита + {b.trial_days} дни
                        </span>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => downloadCsv(b.batch)}
                        data-testid={`batch-download-${b.batch}`}>
                        CSV
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}


/* ─── iter1118b — Ред с бутон „Копирай" за всеки код (без изпращане на имейл) ─── */
function CopyCodeRow({ code, isDark, cellCls }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(`Кодът ${code} е копиран.`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Копирането не успя.");
    }
  };

  return (
    <div
      data-testid={`copy-code-row-${code}`}
      className={`flex items-center justify-between gap-2 p-2 rounded ${
        isDark ? "bg-[hsl(223,47%,12%)]" : "bg-white"
      }`}
    >
      <span className={`font-mono text-xs ${cellCls}`}>{code}</span>
      <Button
        size="sm"
        variant="outline"
        onClick={copy}
        data-testid={`copy-code-btn-${code}`}
      >
        <Copy className="w-3 h-3 mr-1" />
        {copied ? "Копирано" : "Копирай"}
      </Button>
    </div>
  );
}

